import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

function GoogleReviewStars({ rating, size = 16 }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.25 && rating - full < 0.75;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: full }).map((_, i) => (
        <Star key={`f${i}`} className="fill-[#FBBC04] text-[#FBBC04]" style={{ width: size, height: size }} />
      ))}
      {half && (
        <span className="relative" style={{ width: size, height: size }}>
          <Star className="absolute inset-0 text-[#FBBC04]" style={{ width: size, height: size }} />
          <span className="absolute inset-0 overflow-hidden" style={{ width: size / 2 }}>
            <Star className="fill-[#FBBC04] text-[#FBBC04]" style={{ width: size, height: size }} />
          </span>
        </span>
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <Star key={`e${i}`} className="text-gray-300" style={{ width: size, height: size }} />
      ))}
    </span>
  );
}

function ReviewCard({ review }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 h-full">
      <div className="flex items-start gap-3 mb-3">
        {review.profile_photo_url ? (
          <img src={review.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-[#E8E8ED] flex items-center justify-center text-sm font-medium text-[#86868B]">
            {(review.author_name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-[#1D1D1F] truncate">{review.author_name}</p>
          <p className="text-xs text-[#86868B]">{review.relative_time_description}</p>
        </div>
      </div>
      <GoogleReviewStars rating={review.rating || 0} size={14} />
      {review.text && (
        <p className="mt-2 text-sm text-[#3A3A3C] leading-relaxed line-clamp-4">{review.text}</p>
      )}
    </div>
  );
}

export default function GoogleReviewsCarousel({ reviews: data, className = "" }) {
  const reviews = data?.reviews || [];
  const placeId = data?.place_id || "";
  const count = reviews.length;
  const perView = Math.max(1, Math.min(5, Number(data?.reviews_per_view) || 3));
  const GAP = 16;

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [cols, setCols] = useState(1);
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      let c;
      if (w < 640) c = 1;
      else if (w < 1024) c = Math.min(2, perView);
      else c = perView;
      c = Math.min(c, count) || 1;
      setCols(c);
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [perView, count]);

  const loop = count > cols;

  useEffect(() => {
    if (!loop || isPaused) return;
    const timer = setInterval(() => {
      setAnimate(true);
      setIndex((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [loop, isPaused]);

  useEffect(() => {
    if (!loop) {
      if (index !== 0) setIndex(0);
      return;
    }
    if (index >= count) {
      const timer = setTimeout(() => {
        setAnimate(false);
        setIndex((i) => i - count);
      }, 500);
      return () => clearTimeout(timer);
    }
    if (!animate) {
      const raf = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [index, count, loop, animate]);

  if (!count) return null;

  const writeReviewUrl = placeId
    ? `https://search.google.com/local/writereview?placeid=${placeId}`
    : null;

  const googleLogoSvg = (
    <svg viewBox="0 0 24 24" width="20" height="20" className="shrink-0">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );

  const cardWidth = containerWidth > 0 ? (containerWidth - (cols - 1) * GAP) / cols : 0;
  const step = cardWidth + GAP;
  const displayItems = loop ? [...reviews, ...reviews] : reviews;

  const prev = () => { setAnimate(true); setIndex((i) => (i <= 0 ? count - 1 : i - 1)); };
  const next = () => { setAnimate(true); setIndex((i) => i + 1); };

  return (
    <section
      className={`py-12 px-4 md:px-8 bg-[#F9FAFB] ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {googleLogoSvg}
            <div>
              <h3 className="text-lg font-semibold text-[#1D1D1F]">Google Bewertungen</h3>
              {data?.rating != null && (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm font-medium text-[#1D1D1F]">{Number(data.rating).toFixed(1)}</span>
                  <GoogleReviewStars rating={data.rating} size={14} />
                  {data.total_ratings != null && writeReviewUrl ? (
                    <a href={writeReviewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0071E3] hover:underline">
                      ({data.total_ratings} Bewertungen)
                    </a>
                  ) : data.total_ratings != null ? (
                    <span className="text-xs text-[#86868B]">({data.total_ratings})</span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          {loop && (
            <div className="flex items-center gap-1">
              <button type="button" className="p-1.5 rounded-full hover:bg-gray-200 transition-colors" onClick={prev}>
                <ChevronLeft className="w-4 h-4 text-[#86868B]" />
              </button>
              <button type="button" className="p-1.5 rounded-full hover:bg-gray-200 transition-colors" onClick={next}>
                <ChevronRight className="w-4 h-4 text-[#86868B]" />
              </button>
            </div>
          )}
        </div>

        <div className="overflow-hidden" ref={containerRef}>
          <div
            className="flex"
            style={{
              gap: `${GAP}px`,
              transform: `translateX(-${index * step}px)`,
              transition: animate ? "transform 500ms ease-in-out" : "none",
            }}
          >
            {displayItems.map((review, idx) => (
              <div
                key={idx}
                className="flex-shrink-0"
                style={{ width: cardWidth > 0 ? `${cardWidth}px` : `calc((100% - ${(cols - 1) * GAP}px) / ${cols})` }}
              >
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
